"use client"

import { useEffect, useState } from "react"

interface TocItem {
    id: string
    text: string
    level: number
}

interface TableOfContentsProps {
    toc: TocItem[]
}

export default function TableOfContents({ toc }: TableOfContentsProps) {
    const [activeId, setActiveId] = useState<string>("")

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveId(entry.target.id)
                    }
                })
            },
            { rootMargin: "-80px 0px -70% 0px", threshold: 0.1 }
        )

        toc.forEach((item) => {
            const el = document.getElementById(item.id)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [toc])

    if (!toc || toc.length === 0) return null

    return (
        <nav className="space-y-1.5 mt-4 pr-6 overflow-hidden" aria-label="Table of Contents">
            <p
                className="uppercase tracking-wider mb-3"
                style={{
                    fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                    color: "#ffffff",
                    fontSize: "0.95rem",
                    fontWeight: 700,
                }}
            >
                Table of Contents
            </p>
            {toc.map((item) => (
                <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={`block py-1 transition-all duration-200 break-words hyphens-auto ${item.level === 3 ? "pl-3" : "pl-0"
                        } ${activeId === item.id
                            ? "text-white font-medium border-l-2 border-white pl-3"
                            : "text-slate-500 hover:text-slate-300 border-l-2 border-transparent pl-3"
                        }`}
                    style={{
                        fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                        fontWeight: activeId === item.id ? 500 : 400,
                        fontSize: "1rem",
                        lineHeight: "1.45",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                    }}
                >
                    {item.text}
                </a>
            ))}
        </nav>
    )
}
